import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  FormInput,
  ISelectType,
  SwitchInput,
  useFormContext,
} from "@4uhub/lib4uhub";

import MainApiService from "../../../../services/mainApi.service";
import { IGeneric } from "../../../../models/generics";
import { useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";

const SITE_APP_TYPE_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const siteAppTypeService = new MainApiService<IGeneric>(SITE_APP_TYPE_ROUTE);

const TICKET_SERVICE_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServices/";
const ticketService = new MainApiService<ISelectType>(TICKET_SERVICE_ROUTE);

// Tipos de aplicação (SiteAppType no backend) que podem ter o chat 4uChat
const SITE_APP_TYPE_SITE = "1";
export const SITE_APP_TYPE_EXTERNAL_SITE = "6";

export const isChatSiteAppType = (code?: string | null) =>
  code === SITE_APP_TYPE_SITE || code === SITE_APP_TYPE_EXTERNAL_SITE;

export const isChatProfileEnabled = (
  code?: string | null,
  chatTicketService?: { id: string } | null
) => isChatSiteAppType(code) && !!chatTicketService;

const translationPath = "page.register.applications.";

interface ISiteAppProps {
  disable?: boolean;
  /** Exibe o "Serviço de Tickets do 4uChat" (só no cadastro de Aplicações) */
  withChatTicketService?: boolean;
}

const SiteAppType = ({
  disable = false,
  withChatTicketService = false,
}: ISiteAppProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const { id } = useParams();
  const siteAppTypeIdentifier = useMemo(
    () => ({ identifier: "siteAppType" }),
    []
  );

  const type: IGeneric = useWatch({
    control,
    name: "type",
  });

  const isAppMobile = type?.code === "2" || type?.code === "4";
  const showChatTicketService =
    withChatTicketService && isChatSiteAppType(type?.code);

  const chatTicketService: ISelectType | null = useWatch({
    control,
    name: "chatTicketService",
  });

  // O modo tela cheia só vale para o 4uChat: sem Serviço de Tickets ele volta para false
  const showFullScreenMode = showChatTicketService && !!chatTicketService;

  useEffect(() => {
    if (!showFullScreenMode) {
      setValue("fullScreenMode", false);
    }
  }, [showFullScreenMode, setValue]);

  return (
    <>
      <Grid item xs={12} sm={6}>
        <AutoComplete
          params={siteAppTypeIdentifier}
          getOptionLabel={(option: any) => {
            return option.name;
          }}
          onValueChange={(value) => {
            if (value?.name === "Site" && id) {
              setValue("domains", []);
            }
            if (withChatTicketService && !isChatSiteAppType(value?.code)) {
              setValue("chatTicketService", null);
            }
          }}
          size="small"
          label={t(translationPath + "type") || "Type"}
          name="type"
          request={siteAppTypeService.list}
          disable={disable}
          sx={(t) =>
            disable
              ? {
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: t.palette.text.primary,
                  },
                }
              : null
          }
        />
      </Grid>
      {isAppMobile && (
        <Grid item xs={12} sm={12}>
          <FormInput
            size="small"
            name="storeLink"
            fullWidth
            label={
              t(translationPath + "app_link") ||
              "Enter the app link in the store"
            }
            autoFocus
          />
        </Grid>
      )}
      {showChatTicketService && (
        <Grid item xs={12} sm={6}>
          <AutoComplete
            params={{ ticketServiceTypeCode: "3" }}
            getOptionLabel={(option: any) => {
              return option.name;
            }}
            size="small"
            label={
              t(translationPath + "chat_ticket_service") ||
              "Serviço de Tickets do '4uChat'"
            }
            name="chatTicketService"
            request={ticketService.list}
          />
        </Grid>
      )}
      {showFullScreenMode && (
        <Grid item xs={12} sm={6}>
          <SwitchInput
            name="fullScreenMode"
            label={
              t(translationPath + "full_screen_mode") || "Full screen mode"
            }
          />
        </Grid>
      )}
    </>
  );
};

export default SiteAppType;
